'use client';

import LoginButton from '@/components/auth/LoginButton';
import { SidebarMenuContent } from '@/components/layout/SidebarMenuContent';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { MOBILE_VIEW_BREAKPOINT_PX } from '@/utils/breakpoints';
import { Menu } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

/**
 * Simplified sidebar footer for anonymous users.
 * Shows: Login button, Menu (theme, locale, footer links, legal links).
 *        The menu is opened with showCustomize, so the Customize row appears inside it.
 * Does NOT show: Account button, Usage info, Crown/upgrade button
 */
export const SidebarAnonFooter = () => {
  const tHeader = useTranslations('Header');
  const tCommon = useTranslations('Common');
  const [isMobileView, setIsMobileView] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Detect mobile/touch view for responsive menu
  useEffect(() => {
    const handleResize = () => {
      // Match the sidebar's drawer threshold (`mobile-view` variant) — this
      // footer lives inside that drawer, so its sheet-vs-popover choice must
      // flip at the same width (#5452).
      setIsMobileView(window.innerWidth < MOBILE_VIEW_BREAKPOINT_PX);
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Burger menu button (44x44 minimum touch target)
  const MenuButton = (
    <button
      type='button'
      className='flex items-center justify-center rounded-md p-2.5 min-w-[44px] min-h-[44px] hover:bg-interactive-hover active:bg-interactive-active transition-colors cursor-pointer touch-manipulation'
      aria-label={tCommon('openFooterMenu')}
    >
      <Menu size={20} className='text-brand-primary-color' />
    </button>
  );

  return (
    <div className='px-3 py-2 border-t border-line-default flex items-center gap-2 justify-between'>
      {/* Login button */}
      <div className='flex-1'>
        <LoginButton t={tHeader as (key: string) => string} />
      </div>

      {/* Burger menu - Sheet for mobile/touch, Popover for desktop */}
      {isMobileView ? (
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>{MenuButton}</SheetTrigger>
          <SheetContent
            side='bottom'
            className='rounded-t-xl max-h-[70vh] flex flex-col p-0'
          >
            <SheetHeader className='pb-2 px-6 pt-6 shrink-0'>
              <SheetTitle className='text-left'>{tCommon('menu')}</SheetTitle>
            </SheetHeader>
            <div className='overflow-y-auto flex-1 px-6 pb-6'>
              <SidebarMenuContent
                showCustomize
                onItemClick={() => setIsSheetOpen(false)}
                itemHeight='h-10'
              />
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Popover>
          <PopoverTrigger asChild>{MenuButton}</PopoverTrigger>
          <PopoverContent className='w-max p-2 z-[9999]' align='end' side='top'>
            <SidebarMenuContent showCustomize itemHeight='h-10' />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
